<script lang="ts" setup>
  import type { RefComponent } from '@/types'

  import { ref, computed } from 'vue'
  import { useBreakpoints, useElementSize } from '@vueuse/core'

  import { storeToRefs } from 'pinia'
  import { useUiStore } from '@/stores/ui'

  const props = withDefaults(
    defineProps<{
      modalWrapperClasses?: string
      modalClasses?: string
      headerClasses?: string
      bodyClasses?: string
      closeableByBackdropClick?: boolean
      position?: 'center' | 'top'
      mobileHeight?: 'fullscreen' | 'auto'

      imageUrl?: string
      imageAlt?: string
      imageClasses?: string
    }>(),
    {
      headerClasses: 'h-[64px] lg:h-[112px] py-4 border-mbrd border-b px-6',
      bodyClasses: 'px-6',
      modalWrapperClasses: 'md:max-w-[360px] lg:max-w-[440px]',
      modalClasses: 'pb-10',

      closeableByBackdropClick: true,

      position: 'center',
      mobileHeight: 'fullscreen',

      imageUrl: undefined,
      imageAlt: '',
      imageClasses: 'w-[46%] lg:w-1/2',
    }
  )

  const emit = defineEmits<{
    (e: 'close'): void
  }>()

  const wrapperRef: RefComponent<typeof HTMLDivElement> = ref(null)

  const breakpoints = useBreakpoints({
    md: 768,
    lg: 1024,
  })
  const isTabletOrDesktop = breakpoints.greaterOrEqual('md')
  const isDesktop = breakpoints.greaterOrEqual('lg')

  const { height } = useElementSize(wrapperRef)

  const ui = useUiStore()
  const {
    overflowHidden,
    ticker1Visibility,
    ticker2Visibility,
    ticker2VisibilityByPage,
  } = storeToRefs(ui)

  const style = computed(() => {
    let marginTop = null

    if (!isTabletOrDesktop.value && props.position === 'center') return ''
    else if (props.position === 'top') {
      // модалка поиска в самом верху; учитываем ticker 1 и 2 (баннер-полосы)
      const factor =
        (ticker1Visibility.value ? 1 : 0) +
        (ticker2Visibility.value && ticker2VisibilityByPage.value ? 1 : 0)
      const bannerLineHeight = 24 * factor

      const headerHeight = isDesktop.value ? 52 : 48

      marginTop = `${bannerLineHeight + headerHeight}px`
    } else {
      marginTop = `calc(50vh - ${Math.floor(height.value / 2)}px);`
    }

    return `margin-top: ${marginTop};`
  })

  const active = ref(false)

  function show() {
    active.value = true
    overflowHidden.value = true
  }
  function close() {
    active.value = false
    overflowHidden.value = false

    emit('close')
  }
  function handleBackdropClick() {
    if (props.closeableByBackdropClick) close()
  }

  defineExpose({ active, show, close })
</script>

<template>
  <transition name="fade">
    <div
      v-if="active"
      class="fixed left-0 top-0 z-[1055] bg-black/20 h-full w-full overflow-y-auto overflow-x-hidden outline-none"
      :class="[
        {
          flex:
            !isTabletOrDesktop &&
            props.mobileHeight === 'auto' &&
            props.position !== 'top',
        },
      ]"
      tabindex="-1"
      aria-modal="true"
      role="dialog"
      @pointerdown.self="handleBackdropClick"
      @keyup.esc="close"
    >
      <div
        ref="wrapperRef"
        class="w-full max-w-full md:w-auto md:h-auto md:mx-auto relative flex pointer-events-none"
        :class="[
          props.modalWrapperClasses,
          {
            'h-full max-h-full':
              isTabletOrDesktop || props.mobileHeight === 'fullscreen',
          },
          {
            'h-auto': !isTabletOrDesktop && props.mobileHeight === 'auto',
          },
          {
            'mt-auto':
              !isTabletOrDesktop &&
              props.mobileHeight === 'auto' &&
              props.position !== 'top',
          },
        ]"
        :style="style"
      >
        <div
          v-if="props.imageUrl"
          class="hidden md:block shrink-0 aspect-[0.85] lg:aspect-auto"
          :class="props.imageClasses"
        >
          <img
            :src="props.imageUrl"
            :alt="props.imageAlt"
            class="w-full h-full object-cover object-top"
          />
        </div>

        <div
          class="bg-white pointer-events-auto relative flex w-full flex-col"
          :class="props.modalClasses"
        >
          <header
            v-if="$slots.title || $slots.header"
            class="flex"
            :class="props.headerClasses"
          >
            <!--Modal title-->
            <div class="mr-10 flex flex-col">
              <slot name="header" />

              <h2 v-if="$slots.title" class="modal-title">
                <slot name="title" />
              </h2>
            </div>

            <div class="ml-auto">
              <!-- Close Button -->
              <button
                aria-label="close"
                class="close cursor-pointer -mr-1"
                @click.prevent="close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M4 20L20 4M20 20L4 4"
                    stroke="#1F1F1F"
                    stroke-width="1.2"
                    stroke-linecap="square"
                  />
                </svg>
              </button>
            </div>
          </header>

          <!--Modal body-->
          <div v-if="$slots.body" :class="['relative', props.bodyClasses]">
            <slot name="body" />
          </div>

          <!--Modal footer-->
          <div v-if="$slots.footer" class="relative px-6">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
  .modal-title {
    @apply text-c2sm leading-[20px] lg:leading-normal lg:text-cbase font-bold uppercase;
  }

  /* Transitions */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.1s linear;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
